import { getClient, isPreviewMode } from "./sanity";

export interface SanityArticle {
  _id: string;
  title: string;
  slug: { current: string };
  excerpt: string;
  category: {
    _id: string;
    title: string;
    slug: { current: string };
    color?: string;
  };
  author?: {
    _id: string;
    name: string;
    slug?: { current: string };
    role?: string;
    bio?: string;
    avatar?: any;
  };
  tag?: string;
  mainImage?: any;
  lqip?: string;
  publishedAt: string;
  readTime: string;
  body?: any[];
  sourcing_disclosure?: string;
  corrections?: string;
}

const ARTICLES_QUERY = `*[_type == "article" && publishedAt <= now()] | order(publishedAt desc) {
  _id, title, slug, excerpt,
  category->{ _id, title, slug, color },
  author->{ _id, name, slug, role, bio, avatar },
  tag, mainImage, "lqip": mainImage.asset->metadata.lqip, publishedAt, readTime, body, sourcing_disclosure, corrections
}`;

const ARTICLES_PREVIEW_QUERY = `*[_type == "article"] | order(publishedAt desc) {
  _id, title, slug, excerpt,
  category->{ _id, title, slug, color },
  author->{ _id, name, slug, role, bio, avatar },
  tag, mainImage, "lqip": mainImage.asset->metadata.lqip, publishedAt, readTime, body, sourcing_disclosure, corrections
}`;

const ARTICLE_BY_SLUG_QUERY = `*[_type == "article" && slug.current == $slug][0] {
  _id,
  title,
  slug,
  excerpt,
  category->{ _id, title, slug, color },
  author->{ _id, name, slug, role, bio, avatar },
  tag,
  mainImage,
  "lqip": mainImage.asset->metadata.lqip,
  publishedAt,
  readTime,
  body,
  sourcing_disclosure,
  corrections
}`;

const CATEGORIES_QUERY = `*[_type == "category"] | order(title asc) {
  _id,
  title,
  slug,
  description,
  color
}`;

const HOMEPAGE_CONFIG_QUERY = `*[_type == "homepage_config"][0] {
  pinnedStories[]->{ 
    _id, title, slug, excerpt, 
    category->{ _id, title, slug }, 
    author->{ _id, name, slug, role, bio, avatar }, 
    tag, mainImage, publishedAt, readTime 
  },
  marketPulseMarkets[]->{
    _id, title, slug, platform, polymarket_slug, kalshi_ticker, category, priority
  },
  featuredSection {
    label, title, subtitle, linkUrl, linkText, image
  },
  categoryMarketOverrides[] {
    categoryName,
    market->{ _id, title, slug, platform, polymarket_slug, kalshi_ticker, category, priority }
  },
  sectionVisibility
}`;

const PODCAST_EPISODES_QUERY = `*[_type == "podcast_episode"] | order(publishedAt desc)[0..9] {
  _id,
  title,
  guest,
  duration,
  publishedAt,
  audioUrl,
  description,
  episodeNumber
}`;

export interface SanityHomepageConfig {
  pinnedStories?: SanityArticle[];
  marketPulseMarkets?: SanityFeaturedMarket[];
  featuredSection?: {
    label?: string;
    title?: string;
    subtitle?: string;
    linkUrl?: string;
    linkText?: string;
    image?: any;
  };
  categoryMarketOverrides?: {
    categoryName: string;
    market: SanityFeaturedMarket;
  }[];
  sectionVisibility?: Record<string, boolean>;
}

export interface SanityFeaturedMarket {
  _id: string;
  title: string;
  slug?: { current: string };
  platform: string;
  polymarket_slug?: string;
  kalshi_ticker?: string;
  category?: string;
  priority?: number;
}

export interface SanityPodcastEpisode {
  _id: string;
  title: string;
  guest?: string;
  duration?: string;
  publishedAt?: string;
  audioUrl?: string;
  description?: string;
  episodeNumber?: number;
}

export async function fetchArticles(): Promise<SanityArticle[]> {
  const query = isPreviewMode() ? ARTICLES_PREVIEW_QUERY : ARTICLES_QUERY;
  return getClient().fetch(query);
}

export async function fetchArticleBySlug(slug: string): Promise<SanityArticle | null> {
  return getClient().fetch(ARTICLE_BY_SLUG_QUERY, { slug });
}

export async function fetchCategories() {
  return getClient().fetch(CATEGORIES_QUERY);
}

export async function fetchHomepageConfig(): Promise<SanityHomepageConfig | null> {
  return getClient().fetch(HOMEPAGE_CONFIG_QUERY);
}

export async function fetchPodcastEpisodes(): Promise<SanityPodcastEpisode[]> {
  return getClient().fetch(PODCAST_EPISODES_QUERY);
}

/* ── Guides ── */
const GUIDES_QUERY = `*[_type == "guide"] | order(publishedAt desc) {
  _id, title, slug, excerpt, category, level, readTime, publishedAt, body
}`;

export interface SanityGuide {
  _id: string;
  title: string;
  slug: { current: string };
  excerpt?: string;
  category?: string;
  level?: string;
  readTime?: string;
  publishedAt?: string;
  body?: any[];
}

export async function fetchGuides(): Promise<SanityGuide[]> {
  return getClient().fetch(GUIDES_QUERY);
}

/* ── Category Pages ── */
const CATEGORY_PAGES_QUERY = `*[_type == "category_page"] | order(title asc) {
  _id, title, slug, description, icon, sections
}`;

export interface SanityCategoryPage {
  _id: string;
  title: string;
  slug: { current: string };
  description?: string;
  icon?: string;
  sections?: { heading: string; content: string }[];
}

export async function fetchCategoryPages(): Promise<SanityCategoryPage[]> {
  return getClient().fetch(CATEGORY_PAGES_QUERY);
}

/* ── Platform Pages ── */
const PLATFORM_PAGES_QUERY = `*[_type == "platform_page"] | order(headline asc) {
  _id, headline, slug, description, tags, founded, headquarters, regulation, website, sections
}`;

export interface SanityPlatformPage {
  _id: string;
  headline: string;
  slug: { current: string };
  description?: string;
  tags?: string[];
  founded?: string;
  headquarters?: string;
  regulation?: string;
  website?: string;
  sections?: { heading: string; content: string }[];
}

export async function fetchPlatformPages(): Promise<SanityPlatformPage[]> {
  return getClient().fetch(PLATFORM_PAGES_QUERY);
}

/* ── Comparison Pages ── */
const COMPARISON_PAGES_QUERY = `*[_type == "comparison_page"] | order(title asc) {
  _id, title, slug, description, platformA, platformB, sections
}`;

export interface SanityComparisonPage {
  _id: string;
  title: string;
  slug: { current: string };
  description?: string;
  platformA?: string;
  platformB?: string;
  sections?: { heading: string; content: string }[];
}

export async function fetchComparisonPages(): Promise<SanityComparisonPage[]> {
  return getClient().fetch(COMPARISON_PAGES_QUERY);
}

/* ── Legal Pages ── */
const LEGAL_PAGES_QUERY = `*[_type == "legal_page"] | order(title asc) {
  _id, title, slug, body, lastUpdated
}`;

export interface SanityLegalPage {
  _id: string;
  title: string;
  slug: { current: string };
  body?: any[];
  lastUpdated?: string;
}

export async function fetchLegalPages(): Promise<SanityLegalPage[]> {
  return getClient().fetch(LEGAL_PAGES_QUERY);
}

/* ── Polls ── */
const POLLS_QUERY = `*[_type == "poll"] | order(_createdAt desc) {
  _id, question, pollType, category, closesAt, options
}`;

export interface SanityPoll {
  _id: string;
  question: string;
  pollType?: string;
  category?: string;
  closesAt?: string;
  options?: { label: string; votes: number }[];
}

export async function fetchPolls(): Promise<SanityPoll[]> {
  return getClient().fetch(POLLS_QUERY);
}

/* ── Announcements ── */
const ANNOUNCEMENTS_QUERY = `*[_type == "announcement"] | order(date desc) {
  _id, title, slug, date, summary, body, featured
}`;

const ANNOUNCEMENT_BY_SLUG_QUERY = `*[_type == "announcement" && slug.current == $slug][0] {
  _id, title, slug, date, summary, body, featured
}`;

export interface SanityAnnouncement {
  _id: string;
  title: string;
  slug: { current: string };
  date: string;
  summary: string;
  body?: any[];
  featured?: boolean;
}

export async function fetchAnnouncements(): Promise<SanityAnnouncement[]> {
  return getClient().fetch(ANNOUNCEMENTS_QUERY);
}

export async function fetchAnnouncementBySlug(slug: string): Promise<SanityAnnouncement | null> {
  return getClient().fetch(ANNOUNCEMENT_BY_SLUG_QUERY, { slug });
}

/* ── Journalists ── */
const JOURNALISTS_QUERY = `*[_type == "journalist"] | order(name asc) {
  _id, name, slug, role, avatar, bio, fullBio, linkedin, twitter, linkedinUrl, twitterUrl, expertise, previousWork, credentials, citations, education, joinedYear
}`;

export interface SanityJournalist {
  _id: string;
  name: string;
  slug: { current: string };
  role?: string;
  avatar?: any;
  bio?: string;
  fullBio?: any[];
  linkedin?: string;
  twitter?: string;
  linkedinUrl?: string;
  twitterUrl?: string;
  expertise?: string[];
  previousWork?: string;
  credentials?: string[];
  citations?: { outlet: string; context: string }[];
  education?: string;
  joinedYear?: number;
}

export async function fetchJournalists(): Promise<SanityJournalist[]> {
  return getClient().fetch(JOURNALISTS_QUERY);
}

/* ── Editorial Guidelines ── */
const EDITORIAL_GUIDELINES_QUERY = `*[_type == "editorial_guidelines"][0] {
  _id, title, intro, sections
}`;

export interface SanityEditorialGuidelines {
  _id: string;
  title: string;
  intro?: string;
  sections?: { heading: string; content: string }[];
}

export async function fetchEditorialGuidelines(): Promise<SanityEditorialGuidelines | null> {
  return getClient().fetch(EDITORIAL_GUIDELINES_QUERY);
}

/* ── Press Kit ── */
const PRESS_KIT_QUERY = `*[_type == "press_kit"][0] {
  _id, title, intro, boilerplateShort, boilerplateLong, brandGuidelines, keyFacts, socialChannels, mediaContactEmail
}`;

export interface SanityPressKit {
  _id: string;
  title: string;
  intro?: string;
  boilerplateShort?: string;
  boilerplateLong?: string;
  brandGuidelines?: { heading: string; items: string[] }[];
  keyFacts?: { label: string; value: string }[];
  socialChannels?: { label: string; url: string; handle: string }[];
  mediaContactEmail?: string;
}

export async function fetchPressKit(): Promise<SanityPressKit | null> {
  return getClient().fetch(PRESS_KIT_QUERY);
}

/* ── About Page ── */
const ABOUT_PAGE_QUERY = `*[_type == "about_page"][0] {
  _id, title, body, highlights
}`;

export interface SanityAboutPage {
  _id: string;
  title: string;
  body?: any[];
  highlights?: { icon: string; heading: string; description: string }[];
}

export async function fetchAboutPage(): Promise<SanityAboutPage | null> {
  return getClient().fetch(ABOUT_PAGE_QUERY);
}

/* ── Contact Page ── */
const CONTACT_PAGE_QUERY = `*[_type == "contact_page"][0] {
  _id, title, intro, responseTime, contactCards, ctaTitle, ctaSubtitle, formId
}`;

export interface SanityContactPage {
  _id: string;
  title: string;
  intro?: string;
  responseTime?: string;
  contactCards?: { heading: string; description: string; linkText?: string; linkUrl?: string; email?: string }[];
  ctaTitle?: string;
  ctaSubtitle?: string;
  formId?: string;
}

export async function fetchContactPage(): Promise<SanityContactPage | null> {
  return getClient().fetch(CONTACT_PAGE_QUERY);
}

/* ── Feature Modules (Deep Dives) ── */
const FEATURE_MODULES_QUERY = `*[_type == "feature_module" && isActive == true] | order(sort_order asc) {
  _id, label, title, subtitle, linkUrl, linkText, image,
  article->{ _id, title, slug, excerpt, category->{ _id, title, slug, color }, author->{ _id, name, slug, role, bio, avatar }, tag, mainImage, publishedAt, readTime }
}`;

export interface SanityFeatureModule {
  _id: string;
  label?: string;
  title?: string;
  subtitle?: string;
  linkUrl?: string;
  linkText?: string;
  image?: any;
  article?: import("./sanityQueries").SanityArticle;
}

export async function fetchFeatureModules(): Promise<SanityFeatureModule[]> {
  return getClient().fetch(FEATURE_MODULES_QUERY);
}
